import React, { memo } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { getCategoryConfig, formatCurrency } from "@/utils/myMoney";
import { router } from "expo-router";
import { UserFinanceDataResponse, MerchantSpending } from "@/types/financeData";
import { useBrandLogo } from "@/hooks/useBrandLogo";

interface TransactionsCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Get initial letter for fallback logo
const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Helper to assign category to merchant (demo purposes)
const getCategoryForMerchant = (merchantName: string): string => {
  const name = merchantName.toLowerCase();
  if (
    name.includes("doordash") ||
    name.includes("uber eats") ||
    name.includes("grubhub") ||
    name.includes("restaurant")
  ) {
    return "FOOD_AND_DRINK";
  }
  if (
    name.includes("amazon") ||
    name.includes("walmart") ||
    name.includes("target")
  ) {
    return "GENERAL_MERCHANDISE";
  }
  if (
    name.includes("spotify") ||
    name.includes("netflix") ||
    name.includes("hulu") ||
    name.includes("disney")
  ) {
    return "ENTERTAINMENT";
  }
  if (
    name.includes("airline") ||
    name.includes("hotel") ||
    name.includes("airbnb")
  ) {
    return "TRAVEL";
  }
  if (name.includes("uber") || name.includes("lyft") || name.includes("gas")) {
    return "TRANSPORTATION";
  }
  return "GENERAL_SERVICES";
};

// Transaction row item - uses hook for brand logo
const TransactionItem = ({
  merchant,
  isLast,
}: {
  merchant: MerchantSpending;
  isLast: boolean;
}) => {
  const { icon } = useBrandLogo(merchant.merchantName);
  const category = getCategoryForMerchant(merchant.merchantName);
  const categoryConfig = getCategoryConfig([category]);

  return (
    <View>
      <View style={styles.transactionRow}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          {icon ? (
            <Image source={{ uri: icon }} style={styles.logo} />
          ) : (
            <View style={styles.logoFallback}>
              <Text style={styles.logoInitial}>
                {getInitialLetter(merchant.merchantName)}
              </Text>
            </View>
          )}
        </View>

        {/* Name and category */}
        <View style={styles.transactionInfo}>
          <View style={styles.nameRow}>
            <Text style={styles.merchantName} numberOfLines={1}>
              {merchant.merchantName}
            </Text>
            <View
              style={[
                styles.categoryBadge,
                { backgroundColor: categoryConfig.bgColor },
              ]}
            >
              <Text style={styles.categoryText}>
                {categoryConfig.emoji} {categoryConfig.name}
              </Text>
            </View>
          </View>
        </View>

        {/* Amount */}
        <Text style={styles.amount}>{formatCurrency(merchant.amount)}</Text>
      </View>

      {/* Divider */}
      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

export default memo(function TransactionsCard({
  financeData,
}: TransactionsCardProps) {
  const topMerchants = financeData?.analysis?.spending?.topMerchants;

  // Return null if no transactions
  if (!topMerchants?.length) {
    return null;
  }

  const transactions = topMerchants.slice(0, 5);

  const handleViewAll = () => {
    router.push("/(tabs)/my-money/transactions");
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.title}>Transactions to review</Text>
          <Text style={styles.subtitle}>So far today</Text>
        </View>
        <TouchableOpacity onPress={handleViewAll} style={styles.viewAllButton}>
          <Text style={styles.viewAllText}>View all</Text>
        </TouchableOpacity>
      </View>

      {/* Divider */}
      <View style={styles.headerDivider} />

      {/* Transactions list */}
      <View style={styles.transactionsList}>
        {transactions.map((merchant, index) => (
          <TransactionItem
            key={merchant.merchantName + index}
            merchant={merchant}
            isLast={index === transactions.length - 1}
          />
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    ...Shadows.card,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: 8,
  },
  headerLeft: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  subtitle: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  viewAllButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#E1EFE4",
  },
  viewAllText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
  },
  headerDivider: {
    height: 1,
    backgroundColor: "#EBEBEB",
    marginBottom: 8,
  },
  transactionsList: {
    gap: 0,
  },
  transactionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 8,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  transactionInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  merchantName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 20,
  },
  categoryText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#000000",
  },
  amount: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
});
